import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Construction, Delete, Edit, MoreVert } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import PageWithHeader from "../common/PageWithHeader";
import {
  useCreateComsBlockMutation,
  useDeleteComsBlockMutation,
  useGetComsBlocksQuery,
  useUpdateComsBlockMutation,
  ComsBlock,
} from "../../redux/comsApi";

const COMS_NAVY = "#123456";

const emptyForm = {
  code: "",
  name: "",
  description: "",
  development: "",
  projectType: "",
};

const ComsHome: React.FC = () => {
  const navigate = useNavigate();
  const { data: blocks = [], isLoading } = useGetComsBlocksQuery();
  const [createBlock] = useCreateComsBlockMutation();
  const [updateBlock] = useUpdateComsBlockMutation();
  const [deleteBlock] = useDeleteComsBlockMutation();

  // ── Create dialog ──────────────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState("");

  // ── Edit dialog ────────────────────────────────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ComsBlock | null>(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  // ── Delete dialog ──────────────────────────────────────────────────────────
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ComsBlock | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);

  // ── Context menu ───────────────────────────────────────────────────────────
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuBlock, setMenuBlock] = useState<ComsBlock | null>(null);

  const openMenu = (
    e: React.MouseEvent<HTMLButtonElement>,
    block: ComsBlock,
  ) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuBlock(block);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuBlock(null);
  };

  const handleOpenEdit = () => {
    if (!menuBlock) return;
    setEditTarget(menuBlock);
    setEditForm({
      code: menuBlock.code,
      name: menuBlock.name,
      description: menuBlock.description ?? "",
      development: menuBlock.development ?? "",
      projectType: menuBlock.projectType ?? "",
    });
    setEditError("");
    setEditOpen(true);
    closeMenu();
  };

  const handleOpenDelete = () => {
    if (!menuBlock) return;
    setDeleteTarget(menuBlock);
    setDeleteOpen(true);
    closeMenu();
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!createForm.code.trim() || !createForm.name.trim()) {
      setCreateError("Code and Name are required.");
      return;
    }
    if (!createForm.projectType) {
      setCreateError("Project Type is required.");
      return;
    }
    setCreateSaving(true);
    setCreateError("");
    try {
      await createBlock({
        code: createForm.code.trim(),
        name: createForm.name.trim(),
        description: createForm.description.trim() || undefined,
        development: createForm.development.trim() || undefined,
        projectType: createForm.projectType as "type1" | "type2",
      }).unwrap();
      setCreateOpen(false);
      setCreateForm(emptyForm);
    } catch (e: any) {
      setCreateError(e?.data?.error?.message ?? "Failed to create project.");
    } finally {
      setCreateSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!editTarget?.id || !editForm.code.trim() || !editForm.name.trim()) {
      setEditError("Code and Name are required.");
      return;
    }
    // Warn if project type is being changed
    const typeChanging =
      editTarget.projectType &&
      editForm.projectType &&
      editForm.projectType !== editTarget.projectType;
    if (typeChanging) {
      const ok = window.confirm(
        `⚠ Warning: Changing Project Type from "${editTarget.projectType}" to "${editForm.projectType}" will switch the linked catalogue for this project. ` +
          `Existing daily report entries may no longer match. Are you sure?`,
      );
      if (!ok) return;
    }
    setEditSaving(true);
    setEditError("");
    try {
      await updateBlock({
        id: editTarget.id,
        data: {
          code: editForm.code.trim(),
          name: editForm.name.trim(),
          description: editForm.description.trim() || undefined,
          development: editForm.development.trim() || undefined,
          projectType: (editForm.projectType as "type1" | "type2") || undefined,
        },
      }).unwrap();
      setEditOpen(false);
    } catch (e: any) {
      setEditError(e?.data?.error?.message ?? "Failed to update project.");
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleteSaving(true);
    try {
      await deleteBlock(deleteTarget.id).unwrap();
      setDeleteOpen(false);
      setDeleteTarget(null);
    } finally {
      setDeleteSaving(false);
    }
  };

  // ── Group blocks by development ────────────────────────────────────────────
  const grouped: Record<string, ComsBlock[]> = {};
  for (const block of blocks) {
    const dev = block.development ?? "";
    if (!grouped[dev]) grouped[dev] = [];
    grouped[dev].push(block);
  }
  const developments = Object.keys(grouped).sort((a, b) => {
    if (a === "") return 1;
    if (b === "") return -1;
    return a.localeCompare(b);
  });

  return (
    <PageWithHeader>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: COMS_NAVY, color: "#fff", px: 5, py: 4 }}>
        <Typography
          variant="body2"
          sx={{ color: "rgba(255,255,255,0.6)", cursor: "pointer", mb: 1 }}
          onClick={() => navigate("/coms")}
        >
          ← COMS
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Construction sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h1" sx={{ color: "#fff", fontWeight: 700 }}>
              COMS
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
              Construction Operations Management System
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Projects grid ───────────────────────────────────────────────── */}
      <Box px={5} py={4}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h2" sx={{ color: COMS_NAVY, fontWeight: 600 }}>
            Projects
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setCreateForm(emptyForm);
              setCreateError("");
              setCreateOpen(true);
            }}
            sx={{ bgcolor: COMS_NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}
          >
            + New Project
          </Button>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : blocks.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: "center", bgcolor: "#fafafa" }}>
            <Construction sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
            <Typography variant="h5" color="text.secondary">
              No projects yet
            </Typography>
            <Typography color="text.secondary" mt={1}>
              Create your first project to start logging daily reports.
            </Typography>
          </Paper>
        ) : (
          <>
            {developments.map((dev) => (
              <Box key={dev} mb={5}>
                <Typography
                  variant="h6"
                  sx={{
                    color: COMS_NAVY,
                    fontWeight: 700,
                    mb: 2,
                    pb: 1,
                    borderBottom: `2px solid ${COMS_NAVY}`,
                    display: "inline-block",
                    pr: 3,
                  }}
                >
                  {dev || "Unassigned"}
                </Typography>
                <Grid container spacing={3}>
                  {grouped[dev].map((block: ComsBlock) => (
                    <Grid item xs={12} sm={6} md={4} key={block.id}>
                      <Card
                        sx={{
                          borderRadius: 3,
                          boxShadow: "0 2px 12px rgba(18,52,86,0.12)",
                          transition: "0.2s",
                          cursor: "pointer",
                          "&:hover": {
                            boxShadow: "0 6px 20px rgba(18,52,86,0.22)",
                            transform: "translateY(-2px)",
                          },
                        }}
                        onClick={() =>
                          navigate(`/coms/daily-site-report/blocks/${block.id}`)
                        }
                      >
                        <Box
                          sx={{
                            bgcolor: COMS_NAVY,
                            px: 3,
                            py: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Chip
                            label={block.code}
                            size="small"
                            sx={{
                              bgcolor: "rgba(255,255,255,0.2)",
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: "0.85rem",
                            }}
                          />
                          <Typography
                            variant="h6"
                            sx={{ color: "#fff", fontWeight: 600, flex: 1 }}
                          >
                            {block.name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => openMenu(e, block)}
                            sx={{
                              color: "rgba(255,255,255,0.8)",
                              "&:hover": {
                                color: "#fff",
                                bgcolor: "rgba(255,255,255,0.15)",
                              },
                            }}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Box>
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">
                            {block.description ||
                              "Click to open block dashboard"}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </>
        )}
      </Box>

      {/* ── Context menu ────────────────────────────────────────────────── */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
      >
        <MenuItem onClick={handleOpenEdit}>
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={handleOpenDelete} sx={{ color: "error.main" }}>
          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* ── Create Project Dialog ────────────────────────────────────────── */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: COMS_NAVY, color: "#fff" }}>
          New Project
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {createError && (
            <Typography color="error" mb={2} variant="body2">
              {createError}
            </Typography>
          )}
          <TextField
            label="Development"
            placeholder="e.g. Pomona Phase 1"
            fullWidth
            sx={{ mb: 2, mt: 1 }}
            value={createForm.development}
            onChange={(e) =>
              setCreateForm((f) => ({ ...f, development: e.target.value }))
            }
          />
          <TextField
            label="Block Code"
            placeholder="e.g. B1, B2"
            fullWidth
            required
            sx={{ mb: 2 }}
            value={createForm.code}
            onChange={(e) =>
              setCreateForm((f) => ({ ...f, code: e.target.value }))
            }
          />
          <TextField
            label="Block Name"
            placeholder="e.g. Block 2 – North Wing"
            fullWidth
            required
            sx={{ mb: 2 }}
            value={createForm.name}
            onChange={(e) =>
              setCreateForm((f) => ({ ...f, name: e.target.value }))
            }
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={createForm.description}
            onChange={(e) =>
              setCreateForm((f) => ({ ...f, description: e.target.value }))
            }
          />
          <FormControl fullWidth required sx={{ mt: 2 }}>
            <InputLabel>Project Type</InputLabel>
            <Select
              value={createForm.projectType}
              label="Project Type"
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, projectType: e.target.value }))
              }
            >
              <MenuItem value="type1">
                Type 1 — Catalogue Type 1 (Precast Method)
              </MenuItem>
              <MenuItem value="type2">Type 2 — Catalogue Type 2</MenuItem>
            </Select>
            <FormHelperText>
              Required. Determines which shared catalogue is used for Work Done.
            </FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={createSaving}
            sx={{ bgcolor: COMS_NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}
          >
            {createSaving ? "Creating…" : "Create Project"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Project Dialog ──────────────────────────────────────────── */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: COMS_NAVY, color: "#fff" }}>
          Edit Project
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {editError && (
            <Typography color="error" mb={2} variant="body2">
              {editError}
            </Typography>
          )}
          <TextField
            label="Development"
            fullWidth
            sx={{ mb: 2, mt: 1 }}
            value={editForm.development}
            onChange={(e) =>
              setEditForm((f) => ({ ...f, development: e.target.value }))
            }
          />
          <TextField
            label="Block Code"
            fullWidth
            required
            sx={{ mb: 2 }}
            value={editForm.code}
            onChange={(e) =>
              setEditForm((f) => ({ ...f, code: e.target.value }))
            }
          />
          <TextField
            label="Block Name"
            fullWidth
            required
            sx={{ mb: 2 }}
            value={editForm.name}
            onChange={(e) =>
              setEditForm((f) => ({ ...f, name: e.target.value }))
            }
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={editForm.description}
            onChange={(e) =>
              setEditForm((f) => ({ ...f, description: e.target.value }))
            }
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Project Type</InputLabel>
            <Select
              value={editForm.projectType}
              label="Project Type"
              onChange={(e) =>
                setEditForm((f) => ({ ...f, projectType: e.target.value }))
              }
            >
              <MenuItem value="type1">
                Type 1 — Catalogue Type 1 (Precast Method)
              </MenuItem>
              <MenuItem value="type2">Type 2 — Catalogue Type 2</MenuItem>
            </Select>
            <FormHelperText
              sx={{
                color:
                  editForm.projectType !== (editTarget?.projectType ?? "")
                    ? "warning.main"
                    : "inherit",
              }}
            >
              {editForm.projectType !== (editTarget?.projectType ?? "")
                ? "⚠ Changing type switches the linked catalogue — you will be asked to confirm."
                : "Changing this will require confirmation as it switches the linked catalogue."}
            </FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEdit}
            disabled={editSaving}
            sx={{ bgcolor: COMS_NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}
          >
            {editSaving ? "Saving…" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation ──────────────────────────────────────────── */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>
              {deleteTarget?.code} — {deleteTarget?.name}
            </strong>
            ? This will permanently remove the block and all its data.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteSaving}
          >
            {deleteSaving ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageWithHeader>
  );
};

export default ComsHome;
